import { ReactComponent as DownloadIcon } from "assets/icons/download.svg";
import { ReactComponent as StarFilledIcon } from "assets/icons/star-filled.svg";
import { ReactComponent as StarIcon } from "assets/icons/star.svg";
import { ReactComponent as Delete } from "assets/icons/trash.svg";
import { getDisplayDateFormat } from "helpers/DatesHelper";
import { getDropdownValue } from "helpers/DropdownHelper";
import {
    Button,
    ConfirmationModal,
    Field,
    HeaderFilter,
    Loading,
    NotificationSeverityTranslation,
    NotificationSource,
    NotificationSourceTranslation,
} from "modules/common";
import { useEffect, useMemo, useState } from "react";
import { downloadDocument } from "services/documents/documents";
import {
    useDeleteUserNotifications,
    useGetUserNotification,
    useHighlightUserNotification,
} from "../hooks";
import { AppRoutes, RouteParams } from "routing";

interface Props {
    userNotificationId: string;
    onReturn: () => void;
}

const LINK_START = "{LINK_START}";
const LINK_END = "{LINK_END}";

const generateLink = (source: NotificationSource, relationId: string, parts: string[]) => {
    const linkTranslation: Partial<Record<NotificationSource, string>> = {
        [NotificationSource.Education]: `${AppRoutes.Education}?${RouteParams.EducationId}=`,
        [NotificationSource.Record]: `${AppRoutes.Records}?${RouteParams.RecordId}=`,
        [NotificationSource.News]: `${AppRoutes.News}?${RouteParams.NewsId}=`,
        [NotificationSource.Advice]: `${AppRoutes.News}?${RouteParams.AdviceId}=`,
        [NotificationSource.PlantProtection]: `${AppRoutes.PlantProtection}?${RouteParams.RecommendationId}=`,
    };
    return `${parts[0]}<a href='${linkTranslation[source]}${relationId}'>${parts[1]}</a>${parts[2]}`;
};

export const UserNotificationDetails = ({ userNotificationId, onReturn }: Props) => {
    // modal state
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

    // notification query
    const {
        data: userNotificationData,
        isFetching: isLoading,
        isError,
    } = useGetUserNotification(userNotificationId);

    const displayMessage = useMemo(
        () => () => {
            if (userNotificationData?.isHtml) {
                return <div dangerouslySetInnerHTML={{ __html: userNotificationData.body }} />;
            } else if (userNotificationData?.isLink) {
                const parts = userNotificationData.body.split(LINK_START);
                const rest = parts[1].split(LINK_END);

                return (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: generateLink(
                                userNotificationData.source,
                                userNotificationData.relationId,
                                [parts[0], ...rest]
                            ),
                        }}
                    />
                );
            } else {
                return userNotificationData?.body;
            }
        },
        [userNotificationData]
    );

    // highglight and delete notification mutations
    const highlightNotification = useHighlightUserNotification();
    const deleteNotifications = useDeleteUserNotifications();

    // return if not existing
    useEffect(() => {
        if (isError) onReturn();
    }, [isError]);

    return (
        <>
            <HeaderFilter hasBackButton onBackButtonClicked={onReturn}>
                <div className="user-notification-details-header">
                    <div className="header-filter-title">PODACI O PORUCI</div>
                    <div className="user-notificaiton-action">
                        <Button
                            onClick={() => highlightNotification.mutateAsync(userNotificationId)}
                            isIcon
                            size="medium"
                        >
                            {userNotificationData?.isHighlighted ? (
                                <StarFilledIcon />
                            ) : (
                                <StarIcon />
                            )}
                        </Button>

                        <Button
                            onClick={() => setIsConfirmationModalOpen(true)}
                            isIcon
                            size="medium"
                            variant="warning-ghost"
                        >
                            <Delete />
                        </Button>
                    </div>
                </div>
            </HeaderFilter>

            {!isLoading && userNotificationData ? (
                <div className="sent-notification-details">
                    <Field spanFieldColumn={3} fieldColumns={3} horizontal name="NASLOV">
                        {userNotificationData.subject}
                    </Field>

                    <Field spanFieldColumn={3} fieldColumns={3} horizontal name="DATUM SLANJA">
                        {getDisplayDateFormat(userNotificationData.scheduledAt as any, true)}
                    </Field>

                    <Field spanFieldColumn={3} fieldColumns={3} horizontal name="IZVOR">
                        {
                            getDropdownValue(
                                userNotificationData.source,
                                NotificationSourceTranslation
                            ).label
                        }
                    </Field>

                    {userNotificationData.source === NotificationSource.Notification && (
                        <>
                            <Field
                                spanFieldColumn={3}
                                fieldColumns={3}
                                horizontal
                                name="VRSTA PORUKE"
                            >
                                {
                                    getDropdownValue(
                                        userNotificationData.severity,
                                        NotificationSeverityTranslation
                                    ).label
                                }
                            </Field>

                            <Field
                                spanFieldColumn={3}
                                fieldColumns={3}
                                horizontal
                                name="STRUČNO PODRUČJE"
                            >
                                {userNotificationData.professionalField}
                            </Field>
                        </>
                    )}

                    <Field spanFieldColumn={3} fieldColumns={3} horizontal name="SADRŽAJ PORUKE">
                        {displayMessage()}
                    </Field>

                    {userNotificationData.attachments.length !== 0 && (
                        <Field spanFieldColumn={3} fieldColumns={3} horizontal name="PRIVITAK">
                            <div>
                                {userNotificationData.attachments.map(attachment => {
                                    return (
                                        <Button
                                            width="wide"
                                            variant="primary"
                                            key={attachment.id}
                                            size="small"
                                            className="attachment-name"
                                            onClick={() => downloadDocument(attachment.fileUrl, attachment.name)}
                                        >
                                            <div>
                                                <span>{attachment.name}</span>
                                                <DownloadIcon />
                                            </div>
                                        </Button>
                                    );
                                })}
                            </div>
                        </Field>
                    )}
                </div>
            ) : (
                <Loading />
            )}

            <ConfirmationModal
                content="Jeste li sigurni da želite obrisati poruku?"
                isActive={isConfirmationModalOpen}
                onConfirm={() =>
                    deleteNotifications.mutateAsync([userNotificationId]).then(_ => {
                        setIsConfirmationModalOpen(false);
                        onReturn();
                    })
                }
                onCancel={() => setIsConfirmationModalOpen(false)}
            />
        </>
    );
};
